import type { AudioManager } from '../../audio/AudioManager';
import type { PointerEvent2, TouchInput } from '../../input/TouchInput';
import { LOGICAL_HEIGHT, LOGICAL_WIDTH } from '../../render/Renderer';
import { drawBlade } from '../../render/WeaponRenderer';
import type { HeatMap, WeaponDefinition } from '../../types';
import { averageHeat, cloneHeatMap, cool, maxHeat } from '../entities/Weapon';
import { BURN_THRESHOLD } from '../systems/Forge';

const EMBER_TAP_RADIUS = 28;
const EMBER_GRAVITY = 340; // px/s^2 (slower than before for more catch time)
const TROUGH_TOP_Y = 440;
const TROUGH_BOTTOM_Y = 580;
const BLADE_LENGTH = 280;
const MAX_PHASE_SECONDS = 12;

// Plunge preamble.
const BLADE_REST_Y = 230;
const BLADE_BOB_AMPLITUDE = 48;
const BLADE_BOB_PERIOD_SEC = 1.6;
const PLUNGE_HINT_SIZE = 36; // radius of the tap-anywhere hint glow on the blade

type Phase = 'preparing' | 'plunging' | 'snuffing';

interface Ember {
  x: number;
  y: number;
  vx: number;
  vy: number;
  age: number;
  spin: number;
  spinRate: number;
  color: [number, number, number];
  snuffed: boolean;
  lost: boolean;
}

interface SteamParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
}

interface TrailDot {
  x: number;
  y: number;
  life: number;
  maxLife: number;
  color: [number, number, number];
}

export class QuenchSubphase {
  private def: WeaponDefinition;
  private audio: AudioManager;
  private heatMap: HeatMap;
  private done = false;
  private unbindDown: (() => void) | null = null;

  private embers: Ember[] = [];
  private trails: TrailDot[] = [];
  private particles: SteamParticle[] = [];

  private phase: Phase = 'preparing';
  private phaseT = 0;
  private elapsed = 0;
  private extinguishedCount = 0;
  private warpedCount = 0;
  private missedTaps = 0;

  // Plunge quality (0-1) recorded at the moment of tap. Higher = cleaner.
  private plungeQuality = 0;
  /** Bob phase tracker so we can read its value at tap time. */
  private bobPhase = 0;

  // Spawn schedule for snuffing phase.
  private totalEmbers = 0;
  private spawnQueue: { at: number; count: number }[] = [];
  private spawnedSoFar = 0;
  private snuffStartElapsed = 0;
  private hissPlayed = false;
  private bladeCenterY = BLADE_REST_Y;

  constructor(
    def: WeaponDefinition,
    initialHeat: HeatMap,
    input: TouchInput,
    audio: AudioManager,
  ) {
    this.def = def;
    this.audio = audio;
    this.heatMap = cloneHeatMap(initialHeat);
    this.unbindDown = input.onPointerDown((e) => this.handleDown(e));
  }

  /** Compute current vertical position of the blade during the bob. */
  private bobBladeY(): number {
    // y range: BLADE_REST_Y - amplitude (high) .. BLADE_REST_Y + amplitude (low).
    return BLADE_REST_Y + Math.sin(this.bobPhase) * BLADE_BOB_AMPLITUDE;
  }

  /** Quality of plunge given current bob phase. 1 = at the lowest point (closest to water). */
  private plungeQualityAt(phase: number): number {
    // sin(phase) ranges -1..+1; we want max at +1 (lowest).
    const v = Math.sin(phase);
    return Math.max(0, (v + 1) / 2);
  }

  private handleDown(e: PointerEvent2): void {
    if (this.done) return;
    if (this.phase === 'preparing') {
      // Anywhere on the blade region counts; closer to the blade = no penalty.
      // We don't gate on spatial because the verb is "tap to plunge".
      this.plungeQuality = this.plungeQualityAt(this.bobPhase);
      this.bladeCenterY = this.bobBladeY();
      this.phase = 'plunging';
      this.phaseT = 0;
      this.audio.play('strike_perfect');
      this.scheduleSnuff();
      return;
    }
    if (this.phase !== 'snuffing') return;

    let bestIdx = -1;
    let bestDist = Infinity;
    for (let i = 0; i < this.embers.length; i++) {
      const em = this.embers[i]!;
      if (em.snuffed || em.lost) continue;
      const dx = e.pos.x - em.x;
      const dy = e.pos.y - em.y;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    if (bestIdx >= 0 && bestDist <= EMBER_TAP_RADIUS) {
      const em = this.embers[bestIdx]!;
      em.snuffed = true;
      this.extinguishedCount++;
      this.spawnSnuffBurst(em.x, em.y, em.color);
      this.audio.play('strike_perfect');
    } else {
      this.missedTaps++;
      this.audio.play('strike_miss');
    }
  }

  /** Compute total ember count and burst schedule using the (now committed) plunge quality. */
  private scheduleSnuff(): void {
    const avg = averageHeat(this.heatMap);
    const max = maxHeat(this.heatMap);
    // Same base scaling as before, then plunge-quality reduces the count.
    let count = 6 + Math.round(Math.max(0, avg) * 5);
    const burnt = this.heatMap.samples.filter((s) => s > BURN_THRESHOLD).length;
    count += Math.min(4, burnt);
    if (max > 1.05) count += 2;
    // Up to -3 embers from a clean plunge, +3 from a chaotic one (relative to mid).
    const plungeAdj = Math.round((0.5 - this.plungeQuality) * 6);
    count = Math.min(18, Math.max(5, count + plungeAdj));
    this.totalEmbers = count;

    let remaining = count;
    let t = 0;
    const initialBurst = Math.min(4, Math.max(2, Math.round(count * 0.35)));
    this.spawnQueue.push({ at: t, count: initialBurst });
    remaining -= initialBurst;
    t += 0.55;
    while (remaining > 0) {
      const size = Math.min(remaining, 1 + Math.floor(Math.random() * 3));
      this.spawnQueue.push({ at: t, count: size });
      remaining -= size;
      // Cleaner plunges → calmer rhythm.
      const gap = 0.45 + Math.random() * 0.5 + (1 - this.plungeQuality) * -0.1;
      t += Math.max(0.25, gap);
    }
  }

  private spawnBurst(count: number): void {
    const heatBias = Math.min(1, averageHeat(this.heatMap) + 0.3);
    // Plunge quality dampens both speed and spread.
    const speedScale = 1.0 - (this.plungeQuality - 0.5) * 0.3; // ~0.85 clean .. 1.15 chaotic
    for (let i = 0; i < count; i++) {
      const xRange = LOGICAL_WIDTH - 100;
      const x = 50 + Math.random() * xRange;
      const y = TROUGH_TOP_Y + 4;
      const vy = -(180 + Math.random() * 160) * speedScale;
      const vx = (Math.random() - 0.5) * 220 * speedScale;
      const yellow = 60 + heatBias * 120;
      const color: [number, number, number] = [255, 130 + Math.floor(yellow * 0.4), 40 + Math.floor(yellow * 0.2)];
      this.embers.push({
        x,
        y,
        vx,
        vy,
        age: 0,
        spin: Math.random() * Math.PI * 2,
        spinRate: (Math.random() - 0.5) * 8,
        color,
        snuffed: false,
        lost: false,
      });
      this.spawnSplash(x);
    }
    this.audio.play('quench_hiss');
  }

  private spawnSplash(x: number): void {
    for (let i = 0; i < 8; i++) {
      const ang = -Math.PI / 2 + (Math.random() - 0.5) * 1.6;
      const speed = 40 + Math.random() * 60;
      this.particles.push({
        x: x + (Math.random() - 0.5) * 8,
        y: TROUGH_TOP_Y + 2,
        vx: Math.cos(ang) * speed,
        vy: Math.sin(ang) * speed,
        life: 0.5 + Math.random() * 0.4,
        maxLife: 0.9,
      });
    }
  }

  private spawnSnuffBurst(x: number, y: number, color: [number, number, number]): void {
    for (let i = 0; i < 16; i++) {
      const ang = Math.random() * Math.PI * 2;
      const speed = 30 + Math.random() * 90;
      this.particles.push({
        x,
        y,
        vx: Math.cos(ang) * speed,
        vy: Math.sin(ang) * speed,
        life: 0.4 + Math.random() * 0.3,
        maxLife: 0.7,
      });
    }
    this.trails.push({ x, y, life: 0.25, maxLife: 0.25, color });
  }

  update(dt: number): void {
    if (this.done) return;
    this.elapsed += dt;
    this.phaseT += dt;

    if (this.phase === 'preparing') {
      // Bob the blade.
      this.bobPhase += (Math.PI * 2 / BLADE_BOB_PERIOD_SEC) * dt;
      this.bladeCenterY = this.bobBladeY();
      // If the player stalls forever, auto-plunge at neutral quality.
      if (this.phaseT > 8) {
        this.plungeQuality = 0.5;
        this.phase = 'plunging';
        this.phaseT = 0;
        this.scheduleSnuff();
      }
      return;
    }

    if (this.phase === 'plunging') {
      // Animate blade dropping into water.
      const fall = Math.min(1, this.phaseT / 0.35);
      const startY = this.bladeCenterY;
      const targetY = TROUGH_TOP_Y + 34;
      this.bladeCenterY = startY + (targetY - startY) * easeInQuad(fall);
      if (!this.hissPlayed && fall > 0.6) {
        this.audio.play('quench_hiss');
        this.hissPlayed = true;
      }
      if (fall >= 1) {
        this.phase = 'snuffing';
        this.phaseT = 0;
        this.snuffStartElapsed = this.elapsed;
      }
      return;
    }

    // Snuffing phase.
    cool(this.heatMap, 0.55 * dt);

    while (this.spawnQueue.length > 0 && this.spawnQueue[0]!.at <= this.elapsed - this.snuffStartElapsed) {
      const burst = this.spawnQueue.shift()!;
      this.spawnBurst(burst.count);
      this.spawnedSoFar += burst.count;
    }

    for (const em of this.embers) {
      if (em.snuffed || em.lost) continue;
      em.age += dt;
      em.vy += EMBER_GRAVITY * dt;
      em.x += em.vx * dt;
      em.y += em.vy * dt;
      em.spin += em.spinRate * dt;
      if (Math.random() < 28 * dt) {
        this.trails.push({
          x: em.x,
          y: em.y,
          life: 0.35,
          maxLife: 0.35,
          color: em.color,
        });
      }
      if (em.y > TROUGH_TOP_Y && em.vy > 0) {
        em.lost = true;
        this.warpedCount++;
        this.spawnSplash(em.x);
      }
      if (em.x < -20 || em.x > LOGICAL_WIDTH + 20) {
        em.lost = true;
        this.warpedCount++;
      }
    }

    for (let i = this.trails.length - 1; i >= 0; i--) {
      const t = this.trails[i]!;
      t.life -= dt;
      if (t.life <= 0) this.trails.splice(i, 1);
    }
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i]!;
      p.life -= dt;
      if (p.life <= 0) {
        this.particles.splice(i, 1);
        continue;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vy -= 22 * dt;
      p.vx *= 1 - dt * 0.6;
    }

    const allSpawned = this.spawnedSoFar >= this.totalEmbers && this.spawnQueue.length === 0;
    const anyAirborne = this.embers.some((e) => !e.snuffed && !e.lost);
    if (allSpawned && !anyAirborne) {
      this.complete();
    } else if (this.elapsed - this.snuffStartElapsed > MAX_PHASE_SECONDS) {
      const unspawned = this.totalEmbers - this.spawnedSoFar;
      this.warpedCount += unspawned;
      for (const em of this.embers) {
        if (!em.snuffed && !em.lost) {
          em.lost = true;
          this.warpedCount++;
        }
      }
      this.complete();
    }
  }

  isComplete(): boolean {
    return this.done;
  }

  getResult(): {
    heatMap: HeatMap;
    extinguished: number;
    total: number;
    warpPenalty: number;
  } {
    // Plunge quality reduces base penalty (clean entry softens overall costs).
    const plungeBonus = (this.plungeQuality - 0.5) * 0.08;
    const warpPenalty = Math.max(
      0,
      Math.min(0.6, this.warpedCount * 0.07 + this.missedTaps * 0.03 - plungeBonus),
    );
    return {
      heatMap: this.heatMap,
      extinguished: this.extinguishedCount,
      total: this.totalEmbers === 0 ? 1 : this.totalEmbers,
      warpPenalty,
    };
  }

  private complete(): void {
    this.done = true;
    this.unbindDown?.();
    this.unbindDown = null;
  }

  render(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = '#0a0c10';
    ctx.fillRect(0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT);

    ctx.fillStyle = '#e6d3a4';
    ctx.font = 'bold 20px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText('Quench & Snuff', LOGICAL_WIDTH / 2, 18);
    ctx.fillStyle = '#a8957a';
    ctx.font = '12px system-ui, sans-serif';
    const subtitle =
      this.phase === 'preparing'
        ? 'Tap to plunge — pick your moment as the blade dips.'
        : this.phase === 'plunging'
          ? '...'
          : 'Tap every ember that leaps from the water.';
    ctx.fillText(subtitle, LOGICAL_WIDTH / 2, 44);

    // Blade rendered at its current Y (bobbing while preparing, dropping while plunging).
    drawBlade(ctx, this.def.shape, this.heatMap, {
      cx: LOGICAL_WIDTH / 2,
      cy: this.bladeCenterY,
      length: BLADE_LENGTH,
      rotation: 0,
    });

    // Preparing-phase visual hints.
    if (this.phase === 'preparing') {
      this.drawPlungeHints(ctx);
    }

    // Water trough always on top of submerged blade portion.
    ctx.fillStyle = '#1a1108';
    ctx.fillRect(20, TROUGH_TOP_Y - 10, LOGICAL_WIDTH - 40, 12);
    ctx.save();
    ctx.fillStyle = 'rgba(20, 40, 56, 0.78)';
    ctx.fillRect(20, TROUGH_TOP_Y, LOGICAL_WIDTH - 40, TROUGH_BOTTOM_Y - TROUGH_TOP_Y);
    ctx.restore();
    ctx.strokeStyle = 'rgba(120,170,200,0.28)';
    ctx.lineWidth = 1;
    for (let i = 0; i < 4; i++) {
      const y = TROUGH_TOP_Y + 14 + i * 10;
      ctx.beginPath();
      for (let x = 30; x <= LOGICAL_WIDTH - 30; x += 6) {
        const yo = Math.sin((x + i * 20 + performance.now() * 0.004) * 0.2) * 1.2;
        if (x === 30) ctx.moveTo(x, y + yo);
        else ctx.lineTo(x, y + yo);
      }
      ctx.stroke();
    }

    // Trails (additive).
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    for (const t of this.trails) {
      const a = Math.max(0, t.life / t.maxLife) * 0.55;
      const [r, g, b] = t.color;
      ctx.fillStyle = `rgba(${r},${g},${b},${a})`;
      ctx.beginPath();
      ctx.arc(t.x, t.y, 3, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    // Steam / splash.
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    for (const p of this.particles) {
      const a = Math.max(0, p.life / p.maxLife) * 0.4;
      ctx.fillStyle = `rgba(220,230,240,${a})`;
      const r = 3 + (1 - p.life / p.maxLife) * 7;
      ctx.beginPath();
      ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    // Embers.
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    for (const em of this.embers) {
      if (em.snuffed || em.lost) continue;
      const [r, g, b] = em.color;
      const haloR = 16 + Math.sin(em.spin * 2) * 2;
      const grad = ctx.createRadialGradient(em.x, em.y, 1, em.x, em.y, haloR);
      grad.addColorStop(0, `rgba(${r},${g},${b},0.95)`);
      grad.addColorStop(0.4, `rgba(${r},${Math.max(80, g - 30)},${Math.max(20, b - 10)},0.55)`);
      grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(em.x, em.y, haloR, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#fff6cc';
      ctx.beginPath();
      ctx.arc(em.x, em.y, 2.5, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    // HUD (snuffing only).
    if (this.phase === 'snuffing') {
      ctx.fillStyle = '#c9b896';
      ctx.font = 'bold 14px system-ui, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(`Caught: ${this.extinguishedCount}/${this.totalEmbers}`, 24, 80);
      if (this.warpedCount > 0) {
        ctx.fillStyle = '#ff7a4a';
        ctx.textAlign = 'right';
        ctx.fillText(`Lost: ${this.warpedCount}`, LOGICAL_WIDTH - 24, 80);
      }
    }
  }

  private drawPlungeHints(ctx: CanvasRenderingContext2D): void {
    const cx = LOGICAL_WIDTH / 2;
    const cy = this.bladeCenterY;
    // Pulsing glow on the blade so the player knows it's tappable.
    const pulse = 0.5 + 0.5 * Math.sin(this.elapsed * 6);
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    const grad = ctx.createRadialGradient(cx, cy, 1, cx, cy, PLUNGE_HINT_SIZE);
    grad.addColorStop(0, `rgba(160, 220, 255, ${0.35 + 0.25 * pulse})`);
    grad.addColorStop(1, 'rgba(160, 220, 255, 0)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, PLUNGE_HINT_SIZE, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Live plunge-quality preview — small gauge near the blade.
    const q = this.plungeQualityAt(this.bobPhase);
    const gaugeW = 80;
    const gaugeH = 6;
    const gx = cx - gaugeW / 2;
    const gy = cy + 50;
    ctx.fillStyle = '#1a1714';
    ctx.fillRect(gx, gy, gaugeW, gaugeH);
    ctx.fillStyle = q > 0.66 ? '#7fffaa' : q > 0.33 ? '#d4a040' : '#a04040';
    ctx.fillRect(gx, gy, gaugeW * q, gaugeH);
    ctx.strokeStyle = '#5a5040';
    ctx.lineWidth = 1;
    ctx.strokeRect(gx, gy, gaugeW, gaugeH);
    ctx.fillStyle = '#a8957a';
    ctx.font = '10px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('PLUNGE TIMING', cx, gy + 14);

    // Big call-to-action.
    ctx.fillStyle = `rgba(230, 211, 164, ${0.6 + 0.4 * pulse})`;
    ctx.font = 'bold 16px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText('TAP TO PLUNGE', cx, TROUGH_TOP_Y - 60);
  }
}

function easeInQuad(t: number): number {
  return t * t;
}
